import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Dialog, Switch } from '@nyce/admin-kit/ui';
import { ErrorBox, Loading, Tabs, useInterval } from '../../components/common';
import { statusApi, type DbMetrics } from '../../api/status';
import type { MetricsRange } from '../../api/pods';
import { errorMessage } from '../../lib/errors';
import { formatBytes, formatCount } from '../../lib/format';
import { UsageChart } from '@nyce/admin-kit/console';
import { METRICS_REFRESH_MS } from '../pods/PodMetrics';

const RANGES: { value: MetricsRange; label: string }[] = [
  { value: '1h', label: '1 h' },
  { value: '6h', label: '6 h' },
  { value: '24h', label: '24 h' },
  { value: '7d', label: '7 d' },
];

/** A 0–1 fraction as a percentage, one decimal below 10 %. */
export const formatPercent = (v: number) => {
  const p = v * 100;
  return `${p < 10 ? Number(p.toFixed(1)) : Math.round(p)} %`;
};

/**
 * CPU, memory and connections of one Cloud SQL / AlloyDB instance from Cloud Monitoring (about a minute behind),
 * auto-refreshed like the pod metrics.
 */
export function DbMetricsDialog({
  instance,
  name,
  onClose,
}: {
  /** The instance's resource name. */
  instance: string;
  name: string;
  onClose: () => void;
}) {
  const [range, setRange] = useState<MetricsRange>('1h');
  const [auto, setAuto] = useState(true);
  const [data, setData] = useState<DbMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const r = await statusApi.dbMetrics(instance, range);
      if (id !== seq.current) return;
      setData(r);
      setError(null);
    } catch (e) {
      if (id === seq.current) setError(errorMessage(e));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [instance, range]);
  useEffect(() => {
    void load();
  }, [load]);
  useInterval(() => void load(), METRICS_REFRESH_MS, auto);

  const chart = { from: data?.from ?? '', to: data?.to ?? '', stepSeconds: data?.stepSeconds ?? 60 };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={860}
      title={`Metrics · ${name}`}
      footer={
        <>
          <Switch checked={auto} onChange={setAuto} label="Auto-refresh 30 s" />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={loading}
            onClick={() => void load()}
          >
            Refresh
          </Button>
        </>
      }
    >
      <div className="oph-usage-bar">
        <Tabs value={range} onChange={setRange} options={RANGES} />
      </div>
      <ErrorBox error={error} />
      {!data && loading && <Loading loading />}
      {data && (
        <div className={`oph-usage-charts${loading ? ' oph-usage-stale' : ''}`} data-testid="db-metrics">
          <UsageChart title="CPU" series={data.cpu} {...chart} format={formatPercent} limitLabel="" />
          {data.memoryUsed && (
            <UsageChart
              title="Memory used"
              series={data.memoryUsed}
              {...chart}
              format={formatBytes}
              limitLabel="Quota"
            />
          )}
          {data.memoryAvailable && (
            <UsageChart
              title="Memory available"
              series={data.memoryAvailable}
              {...chart}
              format={formatBytes}
              limitLabel=""
            />
          )}
          <UsageChart
            title="Connections"
            series={data.connections}
            {...chart}
            format={(v) => formatCount(Math.round(v))}
            limitLabel="Max"
          />
          <p className="nyk-ui-hint">
            Cloud Monitoring,{' '}
            {data.stepSeconds >= 3600 ? `${data.stepSeconds / 3600} h` : `${data.stepSeconds / 60} min`}{' '}
            {data.kind === 'alloydb'
              ? 'averages. CPU is the mean of the serving nodes; memory is the least available on any node (AlloyDB reports no usage).'
              : 'averages. Memory used excludes the cache; connections are summed over the databases.'}
          </p>
        </div>
      )}
    </Dialog>
  );
}
